import React, { useLayoutEffect, useRef, useState } from 'react'
import { theme } from '@percent/lemonade'
import styled from 'styled-components'
import { FullPageFormLayoutProps } from './full-page-form-layout.types'

const DEFAULT_HEADER_HEIGHT = 80
const MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const MEDIUM_SCREEN_MEDIA_QUERY = '(min-width: 1024px)'
const LARGE_SCREEN_MEDIA_QUERY = '(min-width: 1250px)'
const MEDIUM_SCREEN_SIDE_PADDING = '18%'
const LARGE_SCREEN_SIDE_PADDING = '22%'

const StyledWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
  overflow-x: clip;
  background-color: ${theme.semantic.backgroundPrimary};
  padding-left: ${theme.spacing.l};
  padding-right: ${theme.spacing.l};
  padding-bottom: ${theme.spacing.xl};
  @media ${MOBILE_MEDIA_QUERY} {
    padding-left: 0;
    padding-right: 0;
  }
  @media ${MEDIUM_SCREEN_MEDIA_QUERY} {
    padding-left: ${MEDIUM_SCREEN_SIDE_PADDING};
    padding-right: ${MEDIUM_SCREEN_SIDE_PADDING};
  }
  @media ${LARGE_SCREEN_MEDIA_QUERY} {
    padding-left: ${LARGE_SCREEN_SIDE_PADDING};
    padding-right: ${LARGE_SCREEN_SIDE_PADDING};
  }
`

const StyledHeaderWrapper = styled.div`
  z-index: 91;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${theme.spacing.l} ${theme.spacing.xxl};
  background-color: ${theme.semantic.backgroundSecondary};
  border-bottom: 2px solid ${theme.semantic.borderDefault};
  @media ${MOBILE_MEDIA_QUERY} {
    padding: ${theme.spacing.s} ${theme.spacing.m};
    border-bottom: none;
    box-shadow: ${theme.boxShadows.small};
  }
`

const StyledContentWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  border-bottom-left-radius: ${theme.radii.container};
  border-bottom-right-radius: ${theme.radii.container};
  padding: ${theme.spacing.xl};
  background-color: ${theme.semantic.backgroundSecondary};
  @media ${MOBILE_MEDIA_QUERY} {
    padding: ${theme.spacing.l} ${theme.spacing.m};
  }
  @media ${MEDIUM_SCREEN_MEDIA_QUERY} {
    padding: ${theme.spacing.xxl};
  }
`

export const FullPageFormLayout = ({
  actionsHeader,
  children,
}: FullPageFormLayoutProps) => {
  const headerWrapper = useRef<HTMLDivElement | null>(null)
  const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT)

  useLayoutEffect(() => {
    const header = headerWrapper.current

    if (!header) {
      return undefined
    }

    const updateHeaderHeight = () =>
      setHeaderHeight(header.offsetHeight || DEFAULT_HEADER_HEIGHT)

    updateHeaderHeight()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(updateHeaderHeight)
    observer.observe(header)

    return () => observer.disconnect()
  }, [])

  return (
    <StyledWrapper
      data-testid="full-page-form-wrapper"
      style={{
        paddingTop: `${headerHeight}px`,
      }}
    >
      <StyledHeaderWrapper
        data-testid="full-page-form-header-wrapper"
        ref={headerWrapper}
      >
        {actionsHeader}
      </StyledHeaderWrapper>
      <StyledContentWrapper data-testid="full-page-form-content-wrapper">
        {children}
      </StyledContentWrapper>
    </StyledWrapper>
  )
}
